// PRD 05 §2.1 — top-level 2-panel container. Left = tree + filter, right =
// editor. The outer border + rounded corners come from the prototype's
// `seLayout` block; reproduced inline because admin-web has no shared
// "split panel" component yet.

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useProTreeQuery } from '../hooks/use-questionnaire-tree';
import { useQuestionnaireDesignPageStore } from '../store/questionnaire-design-page.store';
import { findPathToItem } from '../utils/pro-tree';
import { QuestionnaireEditorPanel } from './QuestionnaireEditorPanel';
import { QuestionnaireTreePanel } from './QuestionnaireTreePanel';

// SHCT-390 — when PRO Item Design deep-links here via `?item=<pid>`, open the
// tree on that exact item: expand its area/sub ancestors and select it (so its
// questions load), instead of QD's default empty state. Runs once per target
// pid so it doesn't fight the user's later tree clicks.
function useDeepLinkItemSelection() {
  const [searchParams] = useSearchParams();
  const itemParam = searchParams.get('item');
  const { data: tree } = useProTreeQuery();
  const expandNode = useQuestionnaireDesignPageStore((s) => s.expandNode);
  const selectItem = useQuestionnaireDesignPageStore((s) => s.selectItem);
  const appliedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!itemParam || !tree) return;
    if (appliedFor.current === itemParam) return;
    const path = findPathToItem(tree, itemParam);
    // Silent no-op when the item isn't in the shared tree (e.g. unpublished) —
    // QD falls back to its normal empty state.
    if (!path) return;
    appliedFor.current = itemParam;
    // Expand every ancestor (area, sub); the last node is the item itself.
    for (const node of path.slice(0, -1)) expandNode(node.id);
    selectItem(itemParam);
  }, [itemParam, tree, expandNode, selectItem]);
}

export function QuestionnaireDesignView() {
  useDeepLinkItemSelection();

  return (
    <div
      style={{
        display: 'flex',
        gap: 0,
        minHeight: 'calc(100vh - 160px)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        background: 'var(--card)',
      }}
    >
      <QuestionnaireTreePanel />
      <QuestionnaireEditorPanel />
    </div>
  );
}
